/**
 * Inhalte des Benutzerhandbuchs (/dokumentation).
 *
 * Fachliche Quelle: docs/PRD.md (§2 Rollen, §5 Geschäftsregeln G-1…G-39,
 * §6 UI/UX) und docs/features/*.md — bei Änderungen an Geschäftsregeln
 * bitte hier nachziehen.
 */

import type { DocKapitel } from "./types"

export const HANDBUCH: DocKapitel[] = [
  // ===========================================================================
  // Erste Schritte
  // ===========================================================================
  {
    id: "erste-schritte",
    titel: "Erste Schritte",
    kurz: "Anmeldung, Navigation, Rollen und Umgebungen.",
    abschnitte: [
      {
        id: "anmeldung",
        titel: "Anmeldung (Logto)",
        inhalt: [
          "Die Anmeldung läuft über die zentrale RMNC-Benutzerverwaltung (Logto, OIDC). Sie verwenden dieselben Zugangsdaten wie für die anderen RMNC-Apps (z. B. Leitstand, Dokubase) — einmal anmelden gilt per Single Sign-on für alle freigegebenen Apps.",
          "Ihr Anmeldename ist der Benutzername (username) aus Ihrer Personalakte. Welche Apps Sie nutzen dürfen, steuern die App-Freigaben in der Personalakte.",
        ],
        hinweise: [
          "Kein Zugang? Wenden Sie sich an die Personalabteilung — sie setzt die App-Freigabe „Personal“ in Ihrer Personalakte.",
        ],
        verweise: [{ label: "App-Freigaben & Benutzername", anker: "mitarbeiter-app-freigaben" }],
      },
      {
        id: "navigation",
        titel: "Navigation",
        inhalt: [
          "Links befindet sich die Seitenleiste mit allen Modulen (Dashboard, Mitarbeiter, Anwesenheit, Schichtplan, Abwesenheiten, Urlaub & Krank, Schulungen, Vorsorge, Organisation, Dokumente, Betriebskalender, Dokumentation, Stammdaten). Über das Menü-Symbol oben links lässt sie sich auf Icons verkleinern.",
          "In der Kopfzeile sehen Sie das Umgebungs-Badge, rechts Ihr Benutzermenü (Name, Rollen, Abmelden) und das „?“-Symbol: Es öffnet die Dokumentation direkt beim Kapitel der Seite, auf der Sie sich gerade befinden.",
        ],
        verweise: [{ label: "Schnellsuche (⌘K / Strg K)", anker: "erste-schritte-schnellsuche" }],
      },
      {
        id: "schnellsuche",
        titel: "Schnellsuche (⌘K / Strg K)",
        inhalt: [
          "Mit der Tastenkombination ⌘K (Mac) bzw. Strg K (Windows/Linux) öffnen Sie von überall die Schnellsuche — eine Befehlspalette, mit der Sie in Sekunden navigieren, ohne die Maus zu benutzen. Dasselbe erreichen Sie über den Knopf „Suchen oder navigieren…“ oben links in der Kopfzeile. Erneutes Drücken der Tastenkombination oder Escape schließt sie wieder.",
          "Getippt wird einfach der Suchbegriff. Die Palette durchsucht gleichzeitig vier Quellen und zeigt die Treffer gruppiert an: Seiten (alle Module der Seitenleiste), Aktionen (z. B. „Neuen Mitarbeiter anlegen“, „Urlaubsantrag öffnen“, „Krankmeldung öffnen“, „Wochenplan öffnen“ — jeweils nur, wenn Ihre Rolle das erlaubt), Mitarbeiter (Suche nach Namen im Verzeichnis) und Dokumentation (passende Handbuch-Abschnitte).",
        ],
        schritte: [
          "⌘K bzw. Strg K drücken (oder auf „Suchen oder navigieren…“ klicken).",
          "Suchbegriff tippen — Seiten, Aktionen und Doku-Kapitel erscheinen sofort; Mitarbeiter nach einem kurzen Moment.",
          "Mit ↑ und ↓ den gewünschten Treffer wählen.",
          "Mit Enter öffnen — die App springt zur Seite, zum Mitarbeiter oder zum Doku-Abschnitt.",
        ],
        hinweise: [
          "Umlaute und Groß-/Kleinschreibung spielen keine Rolle: „vorsorge“ findet auch „Vörsorge“.",
          "Aktionen und die Seite „Stammdaten“ erscheinen nur, wenn Ihre Rolle sie erlaubt.",
        ],
      },
      {
        id: "rollen",
        titel: "Rollen — wer darf was",
        inhalt: [
          "Jede Aktion in der App ist an Rollen gebunden (vergeben in Logto). Knöpfe und Tabs, die Ihre Rollen nicht erlauben, werden ausgeblendet — durchgesetzt werden die Rechte zusätzlich immer auf dem Server. Die wichtigsten Rollen vereinfacht:",
        ],
        tabelle: {
          kopf: ["Rolle", "Darf (Kurzfassung)"],
          zeilen: [
            ["admin", "Alles, inkl. Löschen, Stammdaten und Identity-Sync"],
            [
              "personal:leitung / personal:pflege",
              "Mitarbeiter, Stellen, Teams und Schichtplan anlegen und ändern; Urlaub genehmigen; Vorsorge pflegen; nur personal:pflege schreibt Schulungen",
            ],
            [
              "personal:basis",
              "Mitarbeiter lesen, Schichtplan lesen/schreiben, Anwesenheitsboard nutzen",
            ],
            [
              "technik/produktion:leitung + :pflege",
              "Rollen-sichtbare Mitarbeiter lesen, Anwesenheitsboard nutzen",
            ],
            [
              "labor/logistik/einkauf/vertrieb:leitung, geschaeftsfuehrung, betriebsleitung",
              "Mitarbeiter und Auswertungen lesen, Dokumente pflegen",
            ],
            ["extern:fachsicherheit", "Mitarbeiter, Vorsorge und Dokumente lesen"],
            ["beauftragung:pflege", "Betriebskalender pflegen (Betriebsurlaub, Hinweise, Overrides)"],
            ["loeschbuddy", "Einzige Rolle neben admin, die Datensätze löschen darf"],
          ],
        },
        hinweise: [
          "Zeilen-Sichtbarkeit: Sie sehen einen Mitarbeiter (und dessen Anwesenheitszeiten) nur, wenn eine Ihrer Rollen in der Sichtbarkeitsliste seiner Stellen steht — oder Sie eine Pauschalrolle wie admin oder personal:leitung/pflege haben.",
        ],
      },
      {
        id: "umgebungen",
        titel: "Umgebungs-Badge (Dev / Stage / Live)",
        inhalt: [
          "Das farbige Badge in der Kopfzeile zeigt, mit welcher Installation Sie arbeiten: Orange = Dev (Entwicklung), Blau = Stage (Test/Abnahme), Grün = Live (Produktion), Grau = Local. Nur auf „Live“ arbeiten Sie mit den echten Produktionsdaten.",
          "Erscheint zusätzlich ein gestricheltes „Dev-Auth“-Badge, läuft die App ohne Logto-Anmeldung im Entwicklungsmodus.",
        ],
      },
    ],
  },

  // ===========================================================================
  // Mitarbeiter & Personalakte
  // ===========================================================================
  {
    id: "mitarbeiter",
    titel: "Mitarbeiter & Personalakte",
    kurz: "Verzeichnis, Akte mit Tabs, Kontaktdaten, App-Freigaben.",
    abschnitte: [
      {
        id: "verzeichnis",
        titel: "Mitarbeiterverzeichnis",
        inhalt: [
          "Die Seite „Mitarbeiter“ zeigt das durchsuchbare Verzeichnis: Volltextsuche über den Namen, Status-Filter (aktiv / krank / Urlaub / ausgeschieden) und der Filter „nur mit Account“. Die Status krank/Urlaub werden live aus den laufenden Abwesenheiten berechnet — es gibt keine manuell gepflegten Flags.",
          "Ein Klick auf eine Zeile öffnet die Personalakte. Neue Mitarbeiter legen Sie über den Button „Neuer Mitarbeiter“ an.",
        ],
        rollen: "Anlegen/Ändern: admin, personal:leitung, personal:pflege — Lesen gemäß Zeilen-Sichtbarkeit",
        seite: { label: "Zum Mitarbeiterverzeichnis", href: "/mitarbeiter" },
      },
      {
        id: "personalakte",
        titel: "Personalakte (Tabs)",
        inhalt: [
          "Die Personalakte bündelt alles zu einem Mitarbeiter in Tabs: Stammdaten (Name, Geburt, Eintritt/Austritt, Beruf) · Kontakt (Rufnummern, E-Mails, Adressen) · Stellen (Zuordnungen mit Tätigkeitsart und Zulagensätzen) · Abwesenheiten · Beauftragungen · Vorsorge (Soll/Ist mit Abgleich) · App-Freigaben · Konto (Benutzername, Logto-Status, Referenz-Check).",
          "Der Beschäftigungsstatus ergibt sich automatisch: aktiv, wenn ein Eintrittsdatum gesetzt ist und das Austrittsdatum leer oder in der Zukunft liegt.",
        ],
        seite: { label: "Zum Mitarbeiterverzeichnis", href: "/mitarbeiter" },
      },
      {
        id: "kontaktdaten",
        titel: "Kontaktdaten & Freigabe",
        inhalt: [
          "Rufnummern, E-Mail-Adressen und Adressen werden direkt in der Akte gepflegt. Jede Zeile hat einen „freigegeben“-Schalter — aus Datenschutzgründen sind neue Einträge zunächst NICHT freigegeben.",
          "Pro Mitarbeiter gibt es genau EINE freigegebene Standard-E-Mail: Über die Aktion „als Standard setzen“ wird die gewählte Adresse freigegeben und alle anderen automatisch zurückgesetzt. Diese Adresse verwendet auch der Identity-Sync für das Logto-Konto.",
          "Leere Kontaktzeilen werden nicht gespeichert — Pflichtfelder müssen ausgefüllt sein.",
        ],
        rollen: "Schreiben: admin, personal:leitung, personal:pflege",
      },
      {
        id: "app-freigaben",
        titel: "App-Freigaben & Benutzername",
        inhalt: [
          "Im Tab „App-Freigaben“ steuern vier Schalter, welche RMNC-Apps der Mitarbeiter nutzen darf: Leitstand, Dokubase, Besuchermanagement und Ninox. Das Setzen oder Entziehen einer Freigabe löst den Identity-Sync aus (Logto-Konto anlegen bzw. Rolle zuweisen/entziehen).",
          "Der Benutzername (username) wird beim Anlegen automatisch aus Vor- und Nachname erzeugt (z. B. max_mustermann; ä→ae, ß→ss) und ist eindeutig. Wichtig: Bei einer späteren Namensänderung ändert sich der Benutzername NICHT automatisch — er ist der Logto-Anmeldename.",
          "Ohne Benutzername kann keine Freigabe gesetzt werden; die App blockiert den Schalter mit einer erklärenden Meldung.",
        ],
        rollen: "Schreiben: admin, personal:leitung, personal:pflege — Identity-Sync-Verwaltung: admin",
      },
      {
        id: "loeschen",
        titel: "Mitarbeiter löschen (Soft-Delete)",
        inhalt: [
          "Mitarbeiter werden nicht hart gelöscht, sondern als gelöscht markiert (Soft-Delete) — die Historie (Anwesenheiten, Schulungen, Abwesenheiten) bleibt erhalten.",
          "Vor dem Offboarding lohnt der Blick in den Konto-Tab: Der Referenz-Check zeigt, wo der Mitarbeiter in anderen Systemen (TimeTab, KVP, Servicetickets, Produktion …) noch referenziert ist.",
        ],
        rollen: "Löschen: nur admin und loeschbuddy",
      },
    ],
  },

  // ===========================================================================
  // Betriebskalender
  // ===========================================================================
  {
    id: "betriebskalender",
    titel: "Betriebskalender",
    kurz: "Berechneter Jahreskalender — gepflegt werden nur Abweichungen.",
    abschnitte: [
      {
        id: "grundprinzip",
        titel: "Grundprinzip: kein „Jahr anlegen“",
        inhalt: [
          "Wichtig zu verstehen: Es gibt KEIN „Jahr anlegen“ oder „Kalender generieren“ mehr. Jedes Jahr ist sofort vollständig berechnet — die niedersächsischen Feiertage (inkl. beweglicher wie Ostern), Heiligabend/Silvester als halbe Arbeitstage und die Karnevalstage als Sondertage kommen aus dem Programmcode.",
          "Gepflegt werden ausschließlich Abweichungen: Betriebsurlaubs-Zeiträume, Hinweise und (selten) Tagtyp-Overrides. Alles andere ergibt sich automatisch.",
          "Bei überlappenden Regeln gilt die feste Rangfolge: Override > Feiertag > Wochenende > Betriebsurlaub > Sondertag > halber Arbeitstag > Arbeitstag. Planbar (für Schichtplanung und Abwesenheiten) sind Arbeitstage und Betriebsurlaubstage.",
        ],
        seite: { label: "Zum Betriebskalender", href: "/betriebskalender" },
      },
      {
        id: "betriebsurlaub",
        titel: "Betriebsurlaub eintragen (Schritt für Schritt)",
        inhalt: [
          "Ein Betriebsurlaub (Werksschließung, z. B. Betriebsferien) wird als EIN Zeitraum-Eintrag gepflegt:",
        ],
        schritte: [
          "Seite „Betriebskalender“ öffnen und oben das Jahr wählen.",
          "In der Kopfzeile auf den Button „Betriebsurlaub“ klicken (sichtbar nur mit Pflege-Berechtigung).",
          "Zeitraum im Kalender-Picker wählen: Klick = Starttag, zweiter Klick = Endtag; alternativ mit gedrückter Maustaste über die Tage ziehen.",
          "Namen vergeben (z. B. „Betriebsferien Sommer 2026“).",
          "Die Vorschau prüfen: Sie zeigt, welche Tage tatsächlich als Betriebsurlaub wirksam werden — Wochenenden und Feiertage im Zeitraum bleiben unberührt (Kaskaden-Rangfolge).",
          "Speichern. Der Zeitraum erscheint farbig im Jahres-Grid und in der Eintragsliste.",
        ],
        rollen: "Pflege: admin, beauftragung:pflege",
        hinweise: [
          "Der Betriebsurlaub im Kalender ist ein Werks-Zustand — er erzeugt noch KEINE persönlichen Abwesenheiten. Dafür gibt es die Aktion „Auf Mitarbeiter anwenden“ (nächster Abschnitt).",
        ],
        seite: { label: "Zum Betriebskalender", href: "/betriebskalender" },
      },
      {
        id: "hinweise-overrides",
        titel: "Hinweise & Overrides",
        inhalt: [
          "Über den Button „Eintrag“ in der Kopfzeile pflegen Sie Hinweise (freier Text zu einem Tag oder Zeitraum, z. B. „Inventur“) und Overrides (erzwingt einen bestimmten Tagtyp für einen Tag — der Override gewinnt über alle anderen Regeln).",
          "Overrides sind der Ausnahmefall; im Normalbetrieb reichen Betriebsurlaub und Hinweise.",
        ],
        rollen: "Pflege: admin, beauftragung:pflege",
      },
      {
        id: "auf-mitarbeiter-anwenden",
        titel: "Aktion „Auf Mitarbeiter anwenden“",
        inhalt: [
          "An jedem Betriebsurlaubs-Eintrag gibt es die Aktion „Auf Mitarbeiter anwenden“: Sie bucht den Zeitraum als individuellen Urlaub (wahlweise auch Frei nach MTV oder unbezahlter Urlaub) für alle aktiven, für Sie sichtbaren Mitarbeiter — je Mitarbeiter EIN Zeitraum-Datensatz mit Konto-Abzug.",
          "Mitarbeiter mit bestehender überlappender Abwesenheit (z. B. Krank) und ausgeschiedene Mitarbeiter werden übersprungen und in der Ergebnis-Tabelle „Übersprungen“ mit Grund aufgelistet. Die Aktion ist idempotent: Ein zweiter Lauf bucht nichts doppelt.",
        ],
        rollen: "Ausführen: admin, personal:leitung, personal:pflege",
        verweise: [
          { label: "Betriebsurlaub auf Mitarbeiter anwenden (Abwesenheiten)", anker: "abwesenheiten-betriebsurlaub-anwenden" },
          { label: "FAQ: Wieso wird beim Mitarbeiter kein Betriebsurlaub angezeigt?", anker: "faq-kein-betriebsurlaub-beim-mitarbeiter" },
        ],
      },
    ],
  },

  // ===========================================================================
  // Schichtplanung
  // ===========================================================================
  {
    id: "schichtplanung",
    titel: "Schichtplanung",
    kurz: "Ein-Klick-Erstellung mit Vorschau; Rücknahme mit Anwesenheits-Schutz; Umplanen mit Konfliktliste.",
    abschnitte: [
      {
        id: "voraussetzungen",
        titel: "Voraussetzungen",
        inhalt: [
          "Bevor Sie einen Schichtplan generieren können, müssen die Stammdaten gepflegt sein (Seite „Stammdaten“):",
        ],
        schritte: [
          "Schichtmodell mit seinen Schichten anlegen (z. B. Früh/Spät/Nacht; Nachtschichten mit gesetztem Nachtschicht-Kennzeichen für die Zulagen).",
          "Teams anlegen — die Rotationsreihenfolge (Reihenfolge-Feld) bestimmt die Rotationsrichtung.",
          "Team-Besetzung pflegen: welche Mitarbeiter-Stellenzuordnungen zu welchem Team gehören. Ohne Besetzung erzeugt die Ausrollung keine Planzeilen.",
        ],
        rollen: "Stammdaten: admin, personal:leitung, personal:pflege (Schichten zusätzlich personal:basis)",
        seite: { label: "Zu den Stammdaten", href: "/admin/stammdaten" },
      },
      {
        id: "erstellen",
        titel: "Schichtplan erstellen (ein Schritt)",
        inhalt: [
          "Der Standardweg ist die Karte „Schichtplan erstellen“ im Tab „Generator“: Sie erstellt Wochenplan (Rotation) UND Tages-Planzeilen zusammen — in einem Vorgang und einer Transaktion, nach einer Vorschau. Die früher getrennten Phasen 1 und 2 brauchen Sie im Normalfall nicht mehr; sie bleiben unter „Erweitert“ verfügbar.",
          "Die Vorschau ist unverbindlich: Sie zeigt die berechnete Rotations-Matrix (Kalenderwoche × Schicht = Team) und eine Prognose, wie viele Wochenpläne und Tageszeilen entstehen würden — gespeichert wird erst mit „Erstellen“. Bereits vorhandene Wochen sind in der Matrix gedimmt und mit „vorhanden“ markiert; sie werden übersprungen (idempotent).",
        ],
        schritte: [
          "Tab „Generator“ → Karte „Schichtplan erstellen“ → „Schichtplan erstellen …“.",
          "Schichtmodell, Jahr und KW-Bereich (von–bis) wählen, dann „Vorschau“.",
          "Wird das Schichtmodell zum ersten Mal gestartet, fragt die App zuerst die Startbelegung ab (welches Team aktuell in welcher Schicht arbeitet) — danach öffnet sich die Vorschau automatisch.",
          "Vorschau prüfen: Rotations-Matrix, Prognose-Zeile (z. B. „12 Wochenpläne + 112 Tageszeilen werden erstellt“) und eventuelle Warnungen (z. B. Teams ohne Besetzung).",
          "„Erstellen“ klicken — das Ergebnis zeigt die Zahlen beider Phasen; über „Zum Planraster“ springen Sie direkt in den fertigen Plan.",
        ],
        hinweise: [
          "Schlägt die Ausrollung fehl, wird auch der Wochenplan zurückgerollt — es bleibt kein halber Plan stehen (eine Transaktion).",
          "Warnung „Teams ohne Besetzung“: Für diese Teams entstehen keine Tageszeilen. Team-Besetzung in den Stammdaten pflegen und einfach erneut erstellen — es werden nur die fehlenden Zeilen ergänzt.",
          "Ein zweiter Lauf über denselben Bereich ist gefahrlos: Die Vorschau zeigt dann alles als „vorhanden“ und die Prognose „0 neu“.",
        ],
        rollen: "Erstellen: admin, personal:leitung, personal:pflege",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
        verweise: [
          { label: "Startbelegung (erster Start)", anker: "schichtplanung-startbelegung" },
          { label: "Planung zurücknehmen", anker: "schichtplanung-zuruecknehmen" },
        ],
      },
      {
        id: "zuruecknehmen",
        titel: "Planung zurücknehmen",
        inhalt: [
          "Die Karte „Planung zurücknehmen“ (Tab „Generator“) ist die kontrollierte Umkehrung von „Schichtplan erstellen“: Sie löscht die Tages-Planzeilen UND die Wochenpläne eines Schichtmodells in einem KW-Bereich — z. B. wenn ein Plan versehentlich für den falschen Zeitraum oder mit falscher Startbelegung erstellt wurde.",
          "Eingebauter Schutz (Guard): Planzeilen, zu denen bereits eine Anwesenheit gebucht ist (gleicher Mitarbeiter, Tag und Schicht), werden NICHT gelöscht — die gebuchte Realität bleibt unantastbar. Diese Tage listet das Ergebnis unter „An diesen Tagen sind bereits Anwesenheiten gebucht — nicht gelöscht“ auf. Ein Wochenplan fällt nur, wenn alle seine Tageszeilen entfernt werden konnten.",
        ],
        schritte: [
          "Tab „Generator“ → Karte „Planung zurücknehmen“ → „Zurücknehmen …“.",
          "Schichtmodell, Jahr und KW-Bereich wählen — denselben Bereich wie beim Erstellen.",
          "Den Warnhinweis lesen und mit „Ja, Planung endgültig zurücknehmen“ bestätigen.",
          "Ergebnis prüfen: Anzahl gelöschter Tageszeilen und Wochenpläne sowie die Tabelle der geschützten (nicht gelöschten) Zeilen.",
        ],
        hinweise: [
          "Die Rücknahme ist endgültig — aber folgenlos wiederholbar: Ein neuer „Schichtplan erstellen“-Lauf baut den Bereich jederzeit wieder auf.",
          "Sind Zeilen wegen gebuchter Anwesenheiten stehen geblieben, blockieren sie auch das Neusetzen der Startbelegung — erst die Anwesenheiten klären, dann erneut zurücknehmen.",
        ],
        rollen: "Zurücknehmen: admin, personal:leitung, personal:pflege (personal_schreiben)",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
        verweise: [{ label: "Schichtplan erstellen (ein Schritt)", anker: "schichtplanung-erstellen" }],
      },
      {
        id: "startbelegung",
        titel: "Schichtplan starten (Startbelegung)",
        inhalt: [
          "Wenn ein Schichtmodell zum ersten Mal generiert wird, fragt der Generator zunächst die Startbelegung ab: welches Team arbeitet aktuell in welcher Schicht. Das ist nötig, damit die Rotation phasenrichtig startet — der berechnete Plan soll bruchlos an den real gelebten Schichtstand anschließen und nicht bei einer beliebigen Phase beginnen.",
          "Aus dieser Startbelegung leitet das System einen festen Rotationsstart (den „Anker“) ab: die Kalenderwoche, ab der die Belegung gilt, plus die Phasen-Verschiebung. Alle künftigen Wochen werden relativ dazu berechnet.",
        ],
        schritte: [
          "Im Tab „Generator“ über „Schichtplan erstellen …“ ein Schichtmodell wählen und „Vorschau“ klicken (gleiches Verhalten im erweiterten Phase-1-Dialog bei „Generieren …“). Beim allerersten Mal öffnet sich automatisch der Dialog „Schichtplan starten — Startbelegung“.",
          "Kalenderwoche prüfen: „Für welche Kalenderwoche gilt diese Belegung?“ = die aktuelle bzw. erste Woche, ab der der Plan gilt. Vorgeschlagen ist die laufende KW.",
          "In der Matrix je Schicht (Früh/Spät/Nacht) das aktuell dort arbeitende Team wählen. Jedes Team darf in genau einer Schicht stehen.",
          "„Startbelegung speichern“ — der Rotationsstart wird gesetzt und die Generierung läuft direkt weiter.",
        ],
        hinweise: [
          "Die Belegung muss eine gültige Rotationsphase sein: jedes Team steht in genau einer Schicht und die Teams sind der Reihe nach (gemäß Rotationsreihenfolge) gegen die Schichten verschoben. Andernfalls meldet der Dialog „keine gültige Rotationsphase“.",
          "Der Rotationsstart lässt sich nur ändern, solange noch KEINE Wochenpläne existieren. Sobald generiert wurde, ist er festgelegt (die Schaltfläche „Startbelegung ändern“ ist dann ausgegraut) — der bestehende Plan bliebe sonst inkonsistent.",
          "Im Generator-Tab zeigt die Kachel „Rotationsstart (Startbelegung)“ jederzeit den aktuellen Stand und erlaubt das Festlegen/Ändern, solange noch nichts generiert wurde.",
        ],
        rollen: "Startbelegung: admin, personal:leitung, personal:pflege",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
        verweise: [{ label: "Phase 1: Wochenplan generieren", anker: "schichtplanung-wochenplan" }],
      },
      {
        id: "wochenplan",
        titel: "Erweitert — Phase 1: Wochenplan generieren (Rotationsformel)",
        inhalt: [
          "Erweiterter Weg für Sonderfälle: Der Normalfall ist „Schichtplan erstellen“ (ein Schritt, siehe oben) — die getrennten Phasen finden Sie im Tab „Generator“ unter dem aufklappbaren Bereich „Erweitert“.",
          "Dort starten Sie „Wochenplan generieren (Phase 1)“: Schichtmodell, Jahr und KW-Bereich wählen. Der Generator erzeugt die Matrix Kalenderwoche × Schicht → Team nach der Rotationsformel:",
          "Team = Teams[(SchichtIndex + WochenOffset) mod Teamanzahl]",
          "Der Wochen-Offset zählt pro Kalenderwoche hoch — dadurch wandert jedes Team Woche für Woche eine Schicht weiter. Beispiel mit 3 Teams (A, B, C) und 3 Schichten:",
        ],
        tabelle: {
          kopf: ["Kalenderwoche", "Früh (Index 0)", "Spät (Index 1)", "Nacht (Index 2)"],
          zeilen: [
            ["KW 10 (Offset 0)", "Team A", "Team B", "Team C"],
            ["KW 11 (Offset 1)", "Team B", "Team C", "Team A"],
            ["KW 12 (Offset 2)", "Team C", "Team A", "Team B"],
          ],
        },
        hinweise: [
          "Die Rotationsrichtung (z. B. Früh→Spät→Nacht statt Früh→Nacht→Spät) steuern Sie über die Reihenfolge-Felder der Schichten und Teams in den Stammdaten.",
          "Kalenderwochen werden als ISO-Wochen gerechnet — auch Jahre mit KW 53 sind korrekt.",
        ],
        rollen: "Generieren: admin, personal:leitung, personal:pflege",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
      },
      {
        id: "ausrollen",
        titel: "Erweitert — Phase 2: Schichtplan ausrollen",
        inhalt: [
          "Erweiterter Weg (Bereich „Erweitert“ im Tab „Generator“): „Schichtplan ausrollen (Phase 2)“ macht aus dem Wochenplan die Tages-Planzeilen: Für jede Wochenplan-Zeile, jede Team-Besetzung und jeden der 7 Kalendertage der Woche entsteht eine Planzeile (Konti-Betrieb — auch Wochenenden und Feiertage werden ausgerollt).",
          "Optional (empfohlen) wählen Sie im Dialog ein Schichtmodell — dann werden nur dessen Wochenpläne ausgerollt; ohne Auswahl alle Wochenpläne im Bereich.",
          "Nach dem Lauf zeigt eine Zusammenfassung, wie viele Planzeilen erzeugt wurden. Das Ergebnis sehen Sie im Tab „Planraster“ (Kalender-Grid, Abwesende ausgegraut mit Grund-Badge) und im Tab „Wochenplan“ (Matrix).",
        ],
        rollen: "Ausrollen: admin, personal:leitung, personal:pflege",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
      },
      {
        id: "umplanen",
        titel: "Umplanen / Ersatz",
        inhalt: [
          "Über „Umplanen / Ersatz“ hängen Sie die Planzeilen eines Mitarbeiters in einem Zeitraum auf eine neue Stellenzuordnung und/oder Schicht um. Der Mitarbeiter ist Pflichtfeld — ohne Auswahl passiert nichts.",
          "Planzeilen, an denen der Mitarbeiter abwesend ist, werden übersprungen und als Konfliktliste zurückgemeldet — es wird nichts stillschweigend überschrieben.",
        ],
        rollen: "Umplanen: admin, personal:leitung, personal:pflege",
      },
      {
        id: "schicht-wechseln-tauschen",
        titel: "Schicht wechseln oder tauschen",
        inhalt: [
          "Für kurzfristige Änderungen an einzelnen Planzeilen gibt es zwei Wege — je nachdem, ob nur EIN Mitarbeiter die Schicht wechselt oder ZWEI Mitarbeiter ihre Schichten gegenseitig tauschen:",
          "Fall A — einseitig versetzen (Umbuchen): Ein Mitarbeiter wird auf eine andere Schicht und/oder Stelle gesetzt, ohne dass ein zweiter Mitarbeiter davon berührt ist. Das ist das „Umbuchen“ (einzelne Planzeile) bzw. „Umplanen / Ersatz“ (mehrere Tage eines Mitarbeiters auf einmal).",
          "Fall B — gegenseitiger Tausch (Schichttausch): Zwei Mitarbeiter tauschen an EINEM Tag ihre Schicht — z. B. A hat Früh, B hat Nacht, und die beiden tauschen, sodass A Nacht und B Früh arbeitet. Das ist der „Schichttausch“ (G-41): ein atomarer Vorgang, bei dem beide Planzeilen in einem Schritt die Schicht wechseln — entweder klappt es für beide oder für keinen.",
        ],
        schritte: [
          "Im Tab „Planraster“ die Ausgangs-Planzeile (den Mitarbeiter-Chip) anklicken — es öffnet sich der Dialog „Planzeile umbuchen“.",
          "Für den gegenseitigen Tausch dort auf „Tauschen …“ klicken. Es öffnet sich der Dialog „Schicht tauschen“, oben mit der gewählten Planzeile (Mitarbeiter, Datum, aktuelle Schicht/Stelle).",
          "Unter „Tauschpartner“ die Gegen-Planzeile wählen (Liste im Format „Name — Stelle — Schicht“; angeboten werden nur Planzeilen desselben Tages mit anderer Schicht und anderem Mitarbeiter).",
          "Die Vorschau „Nach dem Tausch“ prüfen (A → Schicht von B, B → Schicht von A) und „Tauschen“ klicken. Nach Erfolg erscheint eine Bestätigung und das Raster zeigt die getauschten Schichten.",
        ],
        hinweise: [
          "Tauschbar sind nur Planzeilen am SELBEN Tag mit VERSCHIEDENEN Schichten und verschiedenen Mitarbeitern. Ist die Auswahlliste leer, gibt es an diesem Tag keinen passenden Partner (z. B. weil alle anderen in derselben Schicht stehen oder abwesend sind).",
          "Abwesenheits-Schutz: Ist einer der beiden Mitarbeiter am Tauschtag abwesend (z. B. krank), wird der Tausch abgelehnt und der Grund als Meldung angezeigt („… ist am … abwesend“). Es wird nichts stillschweigend geändert.",
          "Der Tausch ist seine eigene Umkehrung: Ein zweiter Tausch derselben beiden Planzeilen stellt den Ausgangszustand wieder her.",
          "Kein Partner nötig, nur ein Wechsel? Dann statt „Tauschen …“ einfach im Umbuch-Dialog die neue Schicht wählen und „Umbuchen“ klicken (Fall A).",
        ],
        rollen: "Umbuchen & Tauschen: admin, personal:leitung, personal:pflege (personal_schreiben)",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
        verweise: [
          { label: "Umplanen / Ersatz (mehrere Tage)", anker: "schichtplanung-umplanen" },
          { label: "Umbuchen (Anwesenheitsboard)", anker: "anwesenheit-umbuchen" },
        ],
      },
      {
        id: "idempotenz",
        titel: "Nochmal generieren? Keine Duplikate",
        inhalt: [
          "„Schichtplan erstellen“ und beide Generator-Phasen sind idempotent: Ein erneuter Lauf über denselben Zeitraum erzeugt KEINE doppelten Wochenpläne oder Planzeilen (Duplikatschutz über eindeutige Schlüssel; Vorschau und Zusammenfassung zeigen dann „0 neu“ bzw. alles als „vorhanden“).",
          "Sie können den Generator also gefahrlos wiederholen, z. B. nachdem eine Team-Besetzung ergänzt wurde — es werden nur die fehlenden Zeilen ergänzt.",
        ],
      },
    ],
  },

  // ===========================================================================
  // Anwesenheitsboard
  // ===========================================================================
  {
    id: "anwesenheit",
    titel: "Anwesenheitsboard",
    kurz: "Tagesgeschäft: Einbuchen, Status melden, Umbuchen, Ausbuchen, Reports.",
    abschnitte: [
      {
        id: "ueberblick",
        titel: "Tagesablauf im Überblick",
        inhalt: [
          "Das Anwesenheitsboard ist das tägliche Cockpit der Schichtleitung. Es zeigt zum gewählten Datum drei Listen: Eingebucht (inkl. eines separaten Abschnitts „nicht ausgebucht (Vortag)“ für Nachtschicht-Überhänge), Abwesend und Ausgebucht.",
          "Typischer Ablauf: Zu Schichtbeginn die geplanten Mitarbeiter einbuchen bzw. für Fehlende einen Status melden, bei Bedarf umbuchen, zum Schichtende ausbuchen.",
        ],
        rollen:
          "Board nutzen: admin, personal:*, technik:*, produktion:*, labor:leitung, logistik:leitung",
        seite: { label: "Zum Anwesenheitsboard", href: "/anwesenheit" },
      },
      {
        id: "einbuchen",
        titel: "Einbuchen",
        inhalt: [
          "„Einbuchen“ wählt eine heutige Schichtplan-Zeile (noch nicht eingebucht, optional nach Schicht gefiltert) und legt die Anwesenheitsbuchung an — Stelle, Arbeitsplatz und Schicht kommen aus der Planzeile.",
          "Ist für den Mitarbeiter eine Abwesenheit gemeldet (außer Rückruf), lehnt die App das Einbuchen mit Hinweis auf den Grund ab.",
          "Bei Status „Anwesend“ bleibt die Buchung offen (Stunden werden beim Ausbuchen erfasst); bei anderen Status wird pauschal mit 8 Stunden gebucht.",
        ],
      },
      {
        id: "status-melden",
        titel: "Status melden",
        inhalt: [
          "Erscheint ein geplanter Mitarbeiter nicht, melden Sie über „Status melden“ den Grund (z. B. Krank, Urlaub). Die Meldung erzeugt automatisch eine Abwesenheit für heute — meldet jemand später einen anderen Grund, wird nur der Grund aktualisiert.",
          "Diese Schnellmeldung ist auch der schnelle Weg der Krankmeldung fürs Tagesgeschäft.",
        ],
        verweise: [{ label: "Krankmeldung (beide Wege)", anker: "urlaub-krank-krankmeldung" }],
      },
      {
        id: "umbuchen",
        titel: "Umbuchen (Schicht-/Stellenwechsel)",
        inhalt: [
          "Über „Umbuchen“ ändern Sie Schicht und/oder Stelle einer einzelnen Planzeile — z. B. wenn ein Mitarbeiter heute an einem anderen Arbeitsplatz aushilft. Auch hier gilt der Abwesenheits-Guard: gemeldete Abwesende (außer Rückruf) können nicht umgebucht werden.",
        ],
      },
      {
        id: "ausbuchen",
        titel: "Ausbuchen & Zulagen",
        inhalt: [
          "Beim „Ausbuchen“ erfassen Sie die geleisteten Stunden (1–12). Wichtig: In diesem Moment werden die Zulagen der Buchung EINGEFROREN (Vertretungs-, Nachtschicht-, Stellen-, Lärm-, Säure-Staub- und Verdrängungszulage) — spätere Änderungen an den Zulagensätzen ändern abgeschlossene Buchungen nicht mehr.",
          "Die Nachtschichtzulage greift nur bei Schichten mit gesetztem Nachtschicht-Kennzeichen. Stunden-Korrekturen im Nachhinein (mit Neuberechnung und Journal-Eintrag) sind Admin bzw. Personal-Leitung/Pflege vorbehalten.",
        ],
      },
      {
        id: "reports",
        titel: "Reports-Tab",
        inhalt: [
          "Der Tab „Reports“ auf der Anwesenheits-Seite bietet die Zeitraum-Recherche: Date-Range-Picker, Status- und Mehrfach-Filter (Schichten/Stellen), Filter „nicht ausgebucht“, KPI-Karten (Σ Stunden, Σ Zulagen) und XLSX-Export.",
          "Auswertungen sind immer jahresgetrennt; für abgeschlossene Buchungen zählen die eingefrorenen Zulagenbeträge, für offene die Live-Berechnung.",
        ],
        rollen: "Reports: Leitungs-Lesezugriff (alle Leitungs-/Personal-Rollen)",
        seite: { label: "Zur Anwesenheit", href: "/anwesenheit" },
      },
    ],
  },

  // ===========================================================================
  // Abwesenheiten & Betriebsurlaub anwenden
  // ===========================================================================
  {
    id: "abwesenheiten",
    titel: "Abwesenheiten",
    kurz: "Einzel- und Massenbuchung, Zeitraum-Picker, Betriebsurlaub anwenden.",
    abschnitte: [
      {
        id: "einzel",
        titel: "Abwesenheit erfassen",
        inhalt: [
          "Auf der Seite „Abwesenheiten“ erfassen Sie Abwesenheitszeiträume direkt (10 Gründe, u. a. Urlaub, Krank, Frei nach MTV, Kur, Freischicht, Kurzarbeit, unbezahlter Urlaub). Eine Abwesenheit ist EIN Zeitraum-Datensatz; wirksam ist sie an Betriebstagen (Arbeitstage und Betriebsurlaubstage).",
          "Überlappende Abwesenheiten desselben Mitarbeiters sind nicht erlaubt — beim Speichern zeigt die App den Konflikt an.",
          "Für den Genehmigungsweg (Antrag → Genehmigung → Konto-Abzug) nutzen Sie stattdessen die Seite „Urlaub & Krank“.",
        ],
        rollen: "Erfassen: admin, personal:leitung, personal:pflege",
        seite: { label: "Zu den Abwesenheiten", href: "/abwesenheiten" },
        verweise: [{ label: "Urlaub & Krank", anker: "urlaub-krank-antrag" }],
      },
      {
        id: "zeitraum-picker",
        titel: "Bedienung des Zeitraum-Pickers",
        inhalt: [
          "Alle Abwesenheits-Dialoge (Urlaub, Krank, Abwesenheit, Betriebsurlaub) nutzen denselben Kalender-Picker:",
        ],
        schritte: [
          "Erster Klick auf einen Tag setzt den Start, zweiter Klick das Ende. Ein Klick VOR den Start beginnt eine neue Auswahl.",
          "Alternativ mit gedrückter Maustaste (oder Finger) über die Tage ziehen — auch über Monatsgrenzen hinweg.",
          "Die Tage sind nach Betriebskalender eingefärbt; Wochenenden, Feiertage und Sondertage sind gedimmt.",
          "Der Live-Zähler unter dem Kalender zeigt „X Betriebstage im Zeitraum (Y Tage übersprungen)“ — genau so rechnet auch der Server. Halbtage zählen mit 0,5.",
        ],
      },
      {
        id: "masse",
        titel: "Massenbuchung & Massen-Löschung",
        inhalt: [
          "Die Massenbuchung legt für einen Mitarbeiter und Zeitraum eine Abwesenheit an und zeigt vorab, welche Betriebstage betroffen sind (Wochenenden/Feiertage werden übersprungen). Die Massen-Löschung entfernt alle Abwesenheiten eines Mitarbeiters im gewählten Zeitraum.",
          "Nach dem Löschen sind abgeleitete Status (krank/im Urlaub) sofort korrekt — sie werden live berechnet.",
        ],
        rollen: "Ausführen: admin, personal:leitung, personal:pflege",
      },
      {
        id: "betriebsurlaub-anwenden",
        titel: "Betriebsurlaub auf Mitarbeiter anwenden",
        inhalt: [
          "Die Massenaktion „Betriebsurlaub anwenden“ (erreichbar vom Betriebskalender-Eintrag und von der Abwesenheiten-Seite) bucht einen Betriebsurlaubs-Zeitraum als individuellen Urlaub für alle aktiven, sichtbaren Mitarbeiter — mit Konto-Abzug über die Live-Berechnung.",
          "Das Ergebnis zeigt die Anzahl gebuchter Mitarbeiter und die Tabelle „Übersprungen“: Mitarbeiter mit überlappender Abwesenheit (z. B. Krank) oder ausgeschiedene Mitarbeiter werden mit Grund gelistet und NICHT gebucht.",
          "Die Aktion ist idempotent — ein zweiter Lauf meldet alle bereits gebuchten Mitarbeiter als übersprungen und bucht nichts doppelt.",
        ],
        rollen: "Ausführen: admin, personal:leitung, personal:pflege",
        verweise: [
          { label: "Betriebskalender: Auf Mitarbeiter anwenden", anker: "betriebskalender-auf-mitarbeiter-anwenden" },
        ],
      },
    ],
  },

  // ===========================================================================
  // Urlaub & Krank
  // ===========================================================================
  {
    id: "urlaub-krank",
    titel: "Urlaub & Krank",
    kurz: "Antragsworkflow, Urlaubskonto, Krankmeldung mit AU.",
    abschnitte: [
      {
        id: "antrag",
        titel: "Urlaubsantrag stellen",
        inhalt: [
          "Im Tab „Anträge“ der Seite „Urlaub & Krank“ stellen Sie einen Urlaubsantrag: Zeitraum im Kalender-Picker wählen, optional Halbtag am ersten und/oder letzten Tag aktivieren, optional eine Vertretung benennen und eine Bemerkung ergänzen.",
          "Der Antrag startet im Status „Beantragt“; die beantragten Tage werden im Urlaubskonto als „Beantragt“ reserviert. Die Team-Übersicht (Monats-Matrix) hilft, Überschneidungen im Team zu erkennen.",
        ],
        seite: { label: "Zu Urlaub & Krank", href: "/urlaub" },
      },
      {
        id: "genehmigung",
        titel: "Genehmigen, Ablehnen, Stornieren",
        inhalt: [
          "Genehmigen und Ablehnen (mit Begründung) übernimmt die Personalabteilung. Mit der Genehmigung entsteht automatisch die Abwesenheit — sie wirkt sofort auf Schichtplan und Anwesenheitsboard.",
          "Stornieren: Ein noch offener Antrag (Beantragt) kann sofort storniert werden. Bei einem bereits GENEHMIGTEN Antrag wird ohne Personal-Berechtigung nur ein Storno ANGEFRAGT — die Personalabteilung bestätigt („Storno bestätigen“), dann werden die Tage ins Konto zurückgebucht.",
        ],
        rollen: "Genehmigen/Ablehnen/Storno bestätigen: admin, personal:leitung, personal:pflege",
      },
      {
        id: "urlaubskonto",
        titel: "Urlaubskonto lesen",
        inhalt: [
          "Das Urlaubskonto gilt pro Jahr und zeigt: Anspruch, Zusatztage, Übertrag aus dem Vorjahr (mit Verfallsdatum — danach zählt der Übertrag nicht mehr), Anfangssaldo, Genommen und Beantragt (reservierte Tage offener Anträge). Oben steht der verbleibende Saldo.",
          "Der Saldo wird live berechnet — Korrekturen an Abwesenheiten wirken sofort auf das Konto. Die Konten pflegt die Personalabteilung im Tab „Konten“ (Anspruch, Zusatztage, Übertrag, Verfall, Anfangssaldo).",
        ],
        rollen: "Konten pflegen/einsehen (alle): admin, personal:leitung, personal:pflege",
      },
      {
        id: "krankmeldung",
        titel: "Krankmeldung — beide Wege",
        inhalt: [
          "Weg 1 — Schnellmeldung im Tagesgeschäft: Die Schichtleitung meldet auf dem Anwesenheitsboard über „Status melden“ den Status Krank; das erzeugt die Abwesenheit für HEUTE. Geeignet, wenn jemand kurzfristig ausfällt.",
          "Weg 2 — formale Krankmeldung: Im Tab „Krank“ der Seite „Urlaub & Krank“ wird die Krankmeldung mit Zeitraum und Typ (z. B. kurz/lang) erfasst. Hier laden Sie auch die Arbeitsunfähigkeitsbescheinigung (AU) hoch — die Liste zeigt je Meldung, ob die AU noch fehlt oder eingereicht ist.",
        ],
        rollen: "Melden: Self-Service bzw. Board-Rollen — Gesundmelden/Schließen: admin, personal:leitung, personal:pflege",
        seite: { label: "Zu Urlaub & Krank", href: "/urlaub" },
        verweise: [{ label: "Anwesenheitsboard: Status melden", anker: "anwesenheit-status-melden" }],
      },
      {
        id: "krank-im-urlaub",
        titel: "Krankheit während des Urlaubs",
        inhalt: [
          "Erkrankt ein Mitarbeiter in einem bereits genehmigten oder gebuchten Urlaub, dürfen die krankheitsbedingten Tage nicht auf den Urlaub angerechnet werden — sofern eine ärztliche Arbeitsunfähigkeitsbescheinigung (AU) vorliegt (§ 9 BUrlG). Die App löst das in einem Schritt auf: Sie brauchen den Urlaub nicht mehr von Hand zu kürzen.",
          "Wählen Sie im Tab „Krank“ über „Krank melden“ Mitarbeiter und Zeitraum. Liegt der Zeitraum ganz oder teilweise im Urlaub, erkennt die App das automatisch und zeigt eine Info-Box: welcher Urlaub wie aufgetrennt wird (z. B. „Urlaub 13.–24.07. → 13.–19.07. + 23.–24.07.“) und wie viele Urlaubstage gutgeschrieben werden.",
          "Bestätigen Sie mit „Urlaub anpassen & melden“, trennt die App den Urlaub um die Kranktage auf, bucht die Krankheit und schreibt die betroffenen Urlaubstage dem Konto wieder gut — alles in einem Vorgang. Direkt danach bietet der Dialog den AU-Upload an; die Bescheinigung ist in diesem Fall erforderlich.",
          "Liegt im Zeitraum bereits eine andere Abwesenheit (z. B. eine frühere Krankmeldung), blockiert die App die automatische Auflösung und weist darauf hin — korrigieren Sie zuerst die kollidierende Abwesenheit. Liegt kein Urlaub im Zeitraum, läuft die normale Krankmeldung ohne Umweg.",
        ],
        schritte: [
          "Tab „Krank“ → „Krank melden“ öffnen.",
          "Mitarbeiter und Krankheitszeitraum wählen.",
          "Erscheint die blaue Info-Box „liegt im Urlaub“, die Vorschau (Auftrennung + gutgeschriebene Tage) prüfen.",
          "Schalter „Urlaub automatisch anpassen und Krankheit eintragen“ aktiv lassen und mit „Urlaub anpassen & melden“ bestätigen.",
          "Im Anschluss die ärztliche AU hochladen (oder „Später hochladen“).",
        ],
        hinweise: [
          "Rechtlicher Hintergrund § 9 BUrlG: Nur mit ärztlichem Attest werden Krankheitstage nicht auf den Urlaub angerechnet — deshalb ist der AU-Upload hier vorgesehen.",
          "Der Schalter lässt sich abschalten, wenn Sie den Urlaub bewusst NICHT auflösen wollen — dann wird nur die Krankheit gebucht (normaler Weg).",
        ],
        rollen: "Melden: Self-Service bzw. Personal-/Board-Rollen (für andere)",
        seite: { label: "Zu Urlaub & Krank", href: "/urlaub" },
        verweise: [
          { label: "Krankmeldung — beide Wege", anker: "urlaub-krank-krankmeldung" },
          { label: "Urlaubskonto lesen", anker: "urlaub-krank-urlaubskonto" },
        ],
      },
      {
        id: "gesundmeldung",
        titel: "Gesundmeldung & Bradford-Faktor",
        inhalt: [
          "Kommt der Mitarbeiter zurück, schließt die Personalabteilung die Krankmeldung über „Gesundmelden/Schließen“ — dabei wird das tatsächliche Enddatum gesetzt.",
          "Der Bradford-Faktor (S² × D: Anzahl der Krankheitsepisoden zum Quadrat mal Gesamttage) wird angezeigt, wenn Sie die Liste auf einen Mitarbeiter und ein Jahr filtern. Viele kurze Ausfälle wiegen dabei schwerer als ein langer.",
        ],
        rollen: "Gesundmelden: admin, personal:leitung, personal:pflege",
      },
    ],
  },

  // ===========================================================================
  // Schulungen & Beauftragungen
  // ===========================================================================
  {
    id: "schulungen",
    titel: "Schulungen & Beauftragungen",
    kurz: "Fälligkeits-Ampel, Erneuern-Flow, Nachweise, Beauftragungsstatus.",
    abschnitte: [
      {
        id: "faelligkeiten",
        titel: "Fälligkeits-Ampel (30/90 Tage)",
        inhalt: [
          "Die Seite „Schulungen“ hat zwei Tabs: den Katalog (Schulungsmaßnahmen mit Prüfungsintervall und Anbieter) und die Fälligkeiten. Das Ablaufdatum errechnet sich automatisch aus Schulungs- bzw. Prüfungsdatum plus Intervall (kalenderecht, das Prüfungsdatum gewinnt).",
          "Die Ampel: ROT = weniger als 30 Tage bis zum Ablauf, ORANGE = weniger als 90 Tage, GRÜN = mehr Zeit. Die Fälligkeitsliste zeigt nur aktive Mitarbeiter, Standardfenster 90 Tage.",
        ],
        rollen: "Schreiben: nur admin und personal:pflege — Lesen: Leitungsrollen + personal:*",
        seite: { label: "Zu den Schulungen", href: "/schulungen" },
      },
      {
        id: "erneuern",
        titel: "Schulung erneuern & Nachweis hochladen",
        inhalt: [
          "Steht eine Wiederholung an, nutzen Sie die Aktion „Erneuern“: Der alte Datensatz wird entwertet (Nachweis ungültig) und ein neuer Datensatz mit Status FEHLT und leeren Daten angelegt — Mitarbeiter und Schulung werden übernommen. Nach der Schulung tragen Sie Datum/Prüfung ein und laden den Nachweis (Zertifikat) hoch.",
        ],
        rollen: "Erneuern/Upload: admin, personal:pflege",
      },
      {
        id: "beauftragungen",
        titel: "Beauftragungen",
        inhalt: [
          "Zeitlich befristete Beauftragungen (z. B. Sicherheitsbeauftragter) pflegen Sie im Tab „Beauftragungen“ der Personalakte; die Beauftragungstypen (mit Gesetzesgrundlage) liegen in den Stammdaten.",
          "Status je Beauftragung: unvollständig (Datum fehlt) / gültig (heute im Zeitraum) / abgelaufen. Am Mitarbeiter erscheint der Gesamtstatus: 📗 alle gültig, 📒 mindestens eine abgelaufen, 📓 keine bzw. unvollständig.",
        ],
        rollen: "Pflegen: admin, personal:leitung, personal:pflege",
      },
    ],
  },

  // ===========================================================================
  // Vorsorge
  // ===========================================================================
  {
    id: "vorsorge",
    titel: "Vorsorge (AMV)",
    kurz: "Soll/Ist je Stelle, Fälligkeits-Cockpit, XLSX-Report mit Mailversand.",
    abschnitte: [
      {
        id: "soll-ist",
        titel: "Soll/Ist je Stelle",
        inhalt: [
          "Welche arbeitsmedizinischen Untersuchungen ein Mitarbeiter braucht (Soll), ergibt sich aus seinen Stellen: Im Tab „Zuordnung“ der Vorsorge-Seite wird je Stelle festgelegt, welche Untersuchungen Pflicht sind. Die Soll-Liste eines Mitarbeiters ist die Vereinigung über alle seine Stellen.",
          "Über „Fehlende anlegen“ (Soll/Ist-Abgleich, auch im Vorsorge-Tab der Personalakte) legt die App für jede Soll-Untersuchung ohne vorhandenen Datensatz genau einen an — der Abgleich ist idempotent, ein zweiter Lauf erzeugt nichts Neues. Duplikate werden auch bei Einzel-Anlage verhindert.",
        ],
        rollen: "Pflegen: admin, personal:leitung, personal:pflege — Lesen zusätzlich extern:fachsicherheit",
        seite: { label: "Zur Vorsorge", href: "/vorsorge" },
      },
      {
        id: "cockpit",
        titel: "Fälligkeits-Cockpit",
        inhalt: [
          "Das Cockpit filtert die Mitarbeiter-Untersuchungen nach Status, Mitarbeiter, Untersuchung und Fälligkeitsfenster (in echten Tagen, z. B. 3/7/10/14 Tage bzw. Monat/Quartal/heute).",
        ],
      },
      {
        id: "report",
        titel: "XLSX-Report & Mailversand",
        inhalt: [
          "Der Monatsreport wird als XLSX exportiert (Zeilen rot markiert, wenn die Folgeuntersuchung im aktuellen Monat fällig und der Status „geplant“ ist) und kann per Mail an eine konfigurierbare Empfängerliste geschickt werden. Der Report wird bei jedem Versand FRISCH erzeugt — es wird nie eine veraltete Datei verschickt.",
        ],
        rollen: "Export/Versand: admin, personal:leitung, personal:pflege",
      },
    ],
  },

  // ===========================================================================
  // Weitere Bereiche
  // ===========================================================================
  {
    id: "weitere-bereiche",
    titel: "Dokumente · Dashboard · Administration",
    kurz: "Dokumente, Organisation, Auswertung und Admin-Stammdaten.",
    abschnitte: [
      {
        id: "dokumente",
        titel: "Dokumente",
        inhalt: [
          "Die Seite „Dokumente“ ist die leichte Dokumentenverwaltung: Dokumente mit Typ, Version, Gültig-ab und Datei bzw. Link, dazu die Kataloge für Arbeitsanweisungen und Stellenbeschreibungen. Arbeitsanweisungen werden den Arbeitsplätzen per Mehrfachauswahl zugeordnet.",
        ],
        rollen: "Lesen: alle Angemeldeten — Schreiben: alle Leitungsrollen + admin — Löschen: admin, loeschbuddy",
        seite: { label: "Zu den Dokumenten", href: "/dokumente" },
      },
      {
        id: "organisation",
        titel: "Organisation",
        inhalt: [
          "Die Seite „Organisation“ bündelt die Strukturdaten: das Organigramm als auf-/zuklappbarer Baum, die Stellen (mit Zulagensatz, Pflicht-Untersuchungen und der Rollen-Sichtbarkeit, die steuert, wer die Mitarbeiter der Stelle sehen darf) und die Arbeitsplätze (mit Gefährdungsbeurteilung und den drei Arbeitsplatz-Zulagen Lärm/Säure-Staub/Verdrängung in €/h und %).",
          "Zum Verhältnis der beiden: Der Arbeitsplatz ist der physische Ort (wo gearbeitet wird), die Stelle die Funktion (was jemand tut). Jede Stelle gehört zu höchstens einem Arbeitsplatz, ein Arbeitsplatz bündelt mehrere Stellen — z. B. gehören Maschinist/-in und Vorarbeiter/-in der Chipsanlage beide zum Arbeitsplatz „Chips“. Zulagen kombinieren sich aus beiden Ebenen: die Orts-Zulagen (Lärm/Säure-Staub/Verdrängung) kommen vom Arbeitsplatz, der Zulagenstundensatz von der Stelle, Vertretungs- und Nachtschichtsätze aus der Stellenzuordnung des Mitarbeiters.",
          "Mitarbeiter werden nicht direkt verplant, sondern über ihre Stellenzuordnung (Mitarbeiter × Stelle, Haupt- oder Zweittätigkeit): Schichtplan, Team-Besetzung und Abwesenheiten hängen an dieser Zuordnung. Anwesenheitsbuchungen halten deshalb Stelle und Arbeitsplatz fest — die Grundlage der Zulagen-Auswertung.",
        ],
        rollen: "Struktur pflegen: admin, personal:leitung, personal:pflege",
        seite: { label: "Zur Organisation", href: "/organisation" },
      },
      {
        id: "dashboard",
        titel: "Auswertung & Dashboard",
        inhalt: [
          "Die Startseite zeigt KPI-Widgets (Anwesenheit heute, Abwesenheiten nach Grund, Schichtbesetzung, fällige Schulungen/Vorsorge/Beauftragungen, Stunden und Zulagen im Monat, Kopfzahlen). Jede Karte verlinkt in die zugehörige Seite mit vorbelegtem Filter.",
          "Die detaillierten Auswertungen (Zeitraum-Reports, Zulagen je Mitarbeiter/Monat, XLSX-Exporte) finden Sie im Reports-Tab der Anwesenheits-Seite.",
        ],
        seite: { label: "Zum Dashboard", href: "/" },
        verweise: [{ label: "Anwesenheit: Reports-Tab", anker: "anwesenheit-reports" }],
      },
      {
        id: "administration",
        titel: "Administration",
        inhalt: [
          "Die Seite „Stammdaten“ (nur für Berechtigte sichtbar) enthält die CRUD-Grids für Teams, Schichtmodelle, Schichten, Beauftragungstypen und Kostenstellen sowie den Identity-Tab: Sync-Protokoll, Fehlerliste und den manuellen Voll-Sync der Benutzerkonten nach Logto.",
          "Der Betriebskalender hat eine eigene Pflege-Rolle: beauftragung:pflege darf (neben admin) Betriebsurlaub, Hinweise und Overrides pflegen — ohne weitere Admin-Rechte.",
        ],
        rollen: "Stammdaten: admin, personal:leitung, personal:pflege — Identity-Sync: nur admin — Betriebskalender: admin, beauftragung:pflege",
        seite: { label: "Zu den Stammdaten", href: "/admin/stammdaten" },
      },
    ],
  },

  // ===========================================================================
  // FAQ
  // ===========================================================================
  {
    id: "faq",
    titel: "FAQ — Häufige Fragen",
    kurz: "Kurzantworten mit Verweisen in die Kapitel.",
    abschnitte: [
      {
        id: "betriebskalender-erstellen",
        titel: "Wie erstelle ich einen Betriebskalender?",
        inhalt: [
          "Gar nicht — das ist nicht nötig. Der Kalender ist berechnend: Jedes Jahr ist mit allen Feiertagen und Sondertagen sofort verfügbar, ohne „Jahr anlegen“ oder Generator-Lauf. Sie pflegen nur die Abweichungen: Betriebsurlaub, Hinweise und (selten) Overrides.",
        ],
        verweise: [{ label: "Betriebskalender: Grundprinzip", anker: "betriebskalender-grundprinzip" }],
      },
      {
        id: "betriebsurlaub-wo",
        titel: "Wo gebe ich Betriebsurlaub ein?",
        inhalt: [
          "Auf der Seite „Betriebskalender“ über den Button „Betriebsurlaub“ in der Kopfzeile (neben der Jahresauswahl). Der Dialog zeigt eine Vorschau der wirksamen Tage — Wochenenden und Feiertage bleiben unberührt. Sichtbar ist der Button nur für admin und beauftragung:pflege.",
        ],
        rollen: "admin, beauftragung:pflege",
        seite: { label: "Zum Betriebskalender", href: "/betriebskalender" },
        verweise: [{ label: "Betriebsurlaub eintragen (Schritt für Schritt)", anker: "betriebskalender-betriebsurlaub" }],
      },
      {
        id: "schichtplan-erstellen",
        titel: "Wie erstelle ich einen Schichtplan?",
        inhalt: [
          "Mit EINEM Vorgang: Im Tab „Generator“ der Schichtplan-Seite die Karte „Schichtplan erstellen“ nutzen — Schichtmodell, Jahr und KW-Bereich wählen, die Vorschau (Rotations-Matrix + Prognose-Zahlen) prüfen und „Erstellen“ klicken. Wochenplan und Tages-Planzeilen entstehen zusammen in einer Transaktion; beim ersten Start wird vorher die Startbelegung erfragt. Voraussetzung sind gepflegte Stammdaten: Schichtmodell mit Schichten, Teams mit Rotationsreihenfolge und Team-Besetzung.",
          "Die getrennten Schritte (Phase 1 „Wochenplan generieren“ und Phase 2 „Ausrollen“) gibt es weiterhin für Sonderfälle — im aufklappbaren Bereich „Erweitert“.",
        ],
        verweise: [
          { label: "Schichtplan erstellen (ein Schritt)", anker: "schichtplanung-erstellen" },
          { label: "Schichtplanung: Voraussetzungen", anker: "schichtplanung-voraussetzungen" },
        ],
      },
      {
        id: "schichtplan-starten",
        titel: "Wie starte ich mit dem Schichtplan?",
        inhalt: [
          "Über „Schichtplan erstellen“ im Tab „Generator“: Beim allerersten Lauf eines Schichtmodells fragt das System vor der Vorschau die Startbelegung ab — welches Team gerade in welcher Schicht arbeitet. Wählen Sie im Dialog „Schichtplan starten“ je Schicht das aktuell dort arbeitende Team und die Kalenderwoche, ab der das gilt (Vorschlag: laufende KW). Danach öffnet sich die Vorschau, und mit „Erstellen“ entstehen Wochenplan und Tagesplan in einem Schritt — die Rotation setzt bruchlos auf dem real gelebten Stand auf.",
          "Der so gesetzte Rotationsstart ist danach fest — er lässt sich nur ändern, solange noch keine Wochenpläne existieren (nach einer kompletten „Planung zurücknehmen“ also wieder).",
        ],
        verweise: [
          { label: "Schichtplan erstellen (ein Schritt)", anker: "schichtplanung-erstellen" },
          { label: "Schichtplan starten (Startbelegung)", anker: "schichtplanung-startbelegung" },
        ],
      },
      {
        id: "schichtplan-zuruecknehmen",
        titel: "Wie nehme ich einen versehentlich erstellten Schichtplan zurück?",
        inhalt: [
          "Im Tab „Generator“ über die Karte „Planung zurücknehmen“: Schichtmodell, Jahr und KW-Bereich wählen und nach dem Warnhinweis bestätigen. Gelöscht werden Tages-Planzeilen und Wochenpläne des Bereichs — AUSSER an Tagen, an denen bereits Anwesenheiten gebucht sind: Diese Zeilen bleiben geschützt stehen und werden im Ergebnis mit Datum, Mitarbeiter und Grund aufgelistet.",
        ],
        rollen: "admin, personal:leitung, personal:pflege (personal_schreiben)",
        verweise: [{ label: "Planung zurücknehmen", anker: "schichtplanung-zuruecknehmen" }],
      },
      {
        id: "rotationsregel",
        titel: "Wie ist die Regel bei der Schichtgenerierung?",
        inhalt: [
          "Die Rotationsformel lautet: Team = Teams[(SchichtIndex + WochenOffset) mod Teamanzahl]. Der Wochen-Offset zählt pro Kalenderwoche hoch, dadurch rotiert jedes Team Woche für Woche eine Schicht weiter. Bei 3 Teams und 3 Schichten: KW 10 → Früh A / Spät B / Nacht C; KW 11 → Früh B / Spät C / Nacht A; KW 12 → Früh C / Spät A / Nacht B. Die Richtung der Rotation steuern Sie über die Reihenfolge-Felder der Schichten und Teams.",
        ],
        verweise: [{ label: "Rotationsformel mit Beispieltabelle", anker: "schichtplanung-wochenplan" }],
      },
      {
        id: "schicht-tauschen-wechseln",
        titel: "Wie tausche/wechsle ich die Schicht eines Mitarbeiters?",
        inhalt: [
          "Im Tab „Planraster“ den Mitarbeiter-Chip anklicken. Soll nur DIESER Mitarbeiter die Schicht wechseln (niemand sonst betroffen), im Dialog „Planzeile umbuchen“ die neue Schicht wählen und „Umbuchen“ klicken.",
          "Sollen ZWEI Mitarbeiter am selben Tag ihre Schichten GEGENSEITIG tauschen (z. B. Früh ↔ Nacht), im Umbuch-Dialog auf „Tauschen …“ klicken, den Tauschpartner aus der Liste wählen (nur Planzeilen desselben Tages mit anderer Schicht) und „Tauschen“ bestätigen. Der Tausch ist atomar — beide Schichten wechseln in einem Schritt. Ist einer der beiden abwesend, wird der Tausch mit Hinweis abgelehnt.",
        ],
        rollen: "admin, personal:leitung, personal:pflege (personal_schreiben)",
        seite: { label: "Zur Schichtplanung", href: "/schichtplan" },
        verweise: [
          { label: "Schicht wechseln oder tauschen (Schritt für Schritt)", anker: "schichtplanung-schicht-wechseln-tauschen" },
        ],
      },
      {
        id: "krank-im-betriebsurlaub",
        titel: "Was passiert, wenn ein Mitarbeiter während des Betriebsurlaubs krank wird?",
        inhalt: [
          "Der Betriebskalender bleibt unverändert — er beschreibt den Werks-Zustand. Entscheidend ist die persönliche Buchung des Mitarbeiters:",
          "Hatte der Mitarbeiter individuellen Urlaub gebucht (z. B. über „Auf Mitarbeiter anwenden“): Der bequemste Weg ist die Krankmeldung im Tab „Krank“ — erkennt die App den Urlaub im Zeitraum, bietet sie mit „Urlaub anpassen & melden“ eine Ein-Klick-Auflösung an. Sie zeigt vorab, wie der Urlaub aufgetrennt wird und wie viele Tage gutgeschrieben werden, trennt den Urlaub um die Kranktage auf, bucht die Krankheit und schreibt die Urlaubstage in einem Vorgang gut; anschließend wird der AU-Upload angeboten.",
          "Alternativ (manueller Weg): den Urlaub für die Krankheitstage kürzen bzw. stornieren und dann die Krankmeldung MIT ärztlicher AU für den Zeitraum buchen. Das Urlaubskonto korrigiert sich automatisch, weil es live berechnet wird.",
          "Rechtlicher Hintergrund (§ 9 BUrlG): Krankheitstage werden nur mit ärztlichem Attest nicht auf den Urlaub angerechnet.",
          "Hatte der Mitarbeiter KEINEN individuellen Urlaub gebucht: einfach die Krankmeldung erfassen — es gibt nichts zu korrigieren.",
        ],
        verweise: [
          { label: "Krankheit während des Urlaubs", anker: "urlaub-krank-krank-im-urlaub" },
          { label: "Krankmeldung — beide Wege", anker: "urlaub-krank-krankmeldung" },
          { label: "Urlaubskonto lesen", anker: "urlaub-krank-urlaubskonto" },
        ],
      },
      {
        id: "krank-melden",
        titel: "Wie wird krank gemeldet?",
        inhalt: [
          "Es gibt zwei Wege: (1) Schnellmeldung durch die Schichtleitung auf dem Anwesenheitsboard („Status melden“ → Krank) — erzeugt die Abwesenheit für heute; dürfen alle Board-Rollen. (2) Formale Krankmeldung im Tab „Krank“ unter „Urlaub & Krank“ mit Zeitraum, Typ und AU-Upload; Gesundmelden/Schließen übernimmt die Personalabteilung (admin, personal:leitung/pflege).",
        ],
        verweise: [{ label: "Krankmeldung — beide Wege", anker: "urlaub-krank-krankmeldung" }],
      },
      {
        id: "kein-betriebsurlaub-beim-mitarbeiter",
        titel: "Wieso wird beim Mitarbeiter kein Betriebsurlaub angezeigt?",
        inhalt: [
          "Weil der Betriebsurlaub im Kalender ein WERKS-Zustand ist, keine persönliche Abwesenheit. Er erscheint als farbiger Hintergrund in den Kalenderansichten, erzeugt aber von sich aus keine Abwesenheit und keinen Konto-Abzug beim Mitarbeiter.",
          "Erst die Aktion „Auf Mitarbeiter anwenden“ (am Betriebsurlaubs-Eintrag im Betriebskalender oder auf der Abwesenheiten-Seite) bucht den Zeitraum als individuellen Urlaub — dann mit Konto-Abzug und sichtbar in der Personalakte.",
        ],
        verweise: [
          { label: "Aktion „Auf Mitarbeiter anwenden“", anker: "betriebskalender-auf-mitarbeiter-anwenden" },
        ],
      },
      {
        id: "einbuchen-geht-nicht",
        titel: "Warum kann ich einen Mitarbeiter nicht einbuchen?",
        inhalt: [
          "Meistens, weil für ihn eine Abwesenheit gemeldet ist (Krank, Urlaub, …) — die App lehnt das Einbuchen dann mit Hinweis auf den Grund ab. Einzige Ausnahme ist der Status „Rückruf“. Prüfen Sie die Liste „Abwesend“ auf dem Board bzw. die Abwesenheiten des Mitarbeiters; ist die Abwesenheit falsch, muss sie erst korrigiert oder gelöscht werden.",
          "Zweite mögliche Ursache: Es gibt für heute keine Schichtplan-Zeile des Mitarbeiters — eingebucht wird immer gegen den Schichtplan.",
        ],
        verweise: [{ label: "Anwesenheitsboard: Einbuchen", anker: "anwesenheit-einbuchen" }],
      },
      {
        id: "button-fehlt",
        titel: "Warum fehlt mir ein Button oder Tab?",
        inhalt: [
          "Aktionen, die Ihre Rollen nicht erlauben, werden ausgeblendet — z. B. sehen nur admin und beauftragung:pflege den „Betriebsurlaub“-Button, und der Tab „Konten“ unter Urlaub & Krank erscheint nur für Personal-Rollen. Ihre Rollen sehen Sie im Benutzermenü oben rechts. Fehlt Ihnen eine Berechtigung, wenden Sie sich an die Administration.",
        ],
        verweise: [{ label: "Rollen — wer darf was", anker: "erste-schritte-rollen" }],
      },
      {
        id: "halbtage",
        titel: "Wie funktionieren Halbtage beim Urlaub?",
        inhalt: [
          "Im Urlaubsantrag können der erste und/oder letzte Tag als Halbtag markiert werden — sie zählen dann mit 0,5 Tagen im Urlaubskonto. Der Zeitraum-Picker rechnet die Halbtags-Gewichtung im Live-Zähler mit; ein Halbtag zählt nur, wenn der Randtag selbst ein anrechenbarer Betriebstag ist (an einem Feiertag gibt es keinen halben Urlaubstag).",
        ],
        verweise: [{ label: "Urlaubsantrag stellen", anker: "urlaub-krank-antrag" }],
      },
      {
        id: "doppelt-generieren",
        titel: "Was passiert, wenn ich den Schichtplan doppelt generiere?",
        inhalt: [
          "Nichts Schlimmes: „Schichtplan erstellen“ und beide Generator-Phasen sind idempotent. Ein erneuter Lauf über denselben Zeitraum erzeugt keine Duplikate — die Vorschau zeigt dann alles als „vorhanden“ und die Zusammenfassung meldet „0 neu“. Neue Zeilen entstehen nur dort, wo vorher etwas fehlte (z. B. nach ergänzter Team-Besetzung).",
        ],
        verweise: [{ label: "Nochmal generieren? Keine Duplikate", anker: "schichtplanung-idempotenz" }],
      },
      {
        id: "testen",
        titel: "Wie kann ich testen? (nur für Administratoren)",
        nurDev: true,
        inhalt: [
          "Dieser Abschnitt gilt nur für Dev-/Test-Umgebungen (Badge „Dev“ oder „Local“ in der Kopfzeile) — niemals auf „Live“ testen.",
          "Im Dev-Modus (Badge „Dev-Auth“) läuft die App ohne Logto: Die Identität kommt aus dem Browser-localStorage (Schlüssel „devUser“, JSON mit sub/name/roles) und ist standardmäßig „Dev Admin“ mit der Rolle admin. Um andere Rollen zu testen, setzen Sie im localStorage z. B. {\"sub\":\"test\",\"name\":\"Test Pflege\",\"roles\":[\"beauftragung:pflege\"]} und laden die Seite neu — die Oberfläche blendet dann genau die Aktionen ein, die diese Rolle darf.",
        ],
        verweise: [{ label: "Rollen — wer darf was", anker: "erste-schritte-rollen" }],
      },
    ],
  },
]
